<template>
    <div class="tlc-rec-page">
        <!-- Header -->
        <div class="tlc-rec-head">
            <div class="tlc-rec-head__left">
                <router-link :to="{ name: 'team-leads-calendar' }" class="tlc-rec-back">
                    <i class="el-icon-arrow-left"></i> Lịch tuần
                </router-link>
                <div>
                    <div class="tlc-rec-title">Lịch cố định &amp; lặp lại</div>
                    <div class="tlc-rec-sub">
                        Tạo lịch <b>một lần</b>, áp dụng cho <b>tất cả các tuần</b> theo khung giờ đã chọn.
                        VD: <i>"T2, T6 · 14:00–17:00: họp phòng TCGV"</i> hoặc <i>"Chị Cẩm Tú: T3, T7 · 08:00–10:00"</i>.
                    </div>
                </div>
            </div>
            <span class="tlc-role" :class="`tlc-role--${role}`">
                <i :class="roleMeta.icon"></i> {{ roleMeta.label }}
            </span>
        </div>

        <!-- Action bar -->
        <div class="tlc-rec-bar">
            <div class="tlc-rec-bar__actions">
                <el-button type="primary" size="small" icon="el-icon-plus" @click="openCreate">
                    Thêm lịch cố định
                </el-button>
            </div>

            <div class="tlc-rec-bar__filters">
                <el-select
                    v-if="showTeamLead"
                    v-model="filterUserId"
                    size="small"
                    filterable
                    clearable
                    placeholder="Tất cả Teamlead"
                    class="tlc-rec-bar__select"
                    @change="reload"
                >
                    <el-option v-for="tl in tlcTeamLeads" :key="tl.id" :label="tl.name" :value="tl.id" />
                </el-select>
                <el-input
                    v-model="keyword"
                    size="small"
                    clearable
                    prefix-icon="el-icon-search"
                    placeholder="Tìm nội dung công việc"
                    class="tlc-rec-bar__search"
                />
            </div>
        </div>

        <!-- Stats -->
        <div class="tlc-rec-stats">
            <div class="tlc-rec-stat">
                <span class="tlc-rec-stat__num">{{ paging.total }}</span>
                <span class="tlc-rec-stat__label">Tổng lịch cố định</span>
            </div>
            <div class="tlc-rec-stat">
                <span class="tlc-rec-stat__num tlc-rec-stat__num--on">{{ teamLeadCount }}</span>
                <span class="tlc-rec-stat__label">Teamlead áp dụng (trang này)</span>
            </div>
            <div class="tlc-rec-stat">
                <span class="tlc-rec-stat__num">{{ weeklySlotCount }}</span>
                <span class="tlc-rec-stat__label">Lượt lặp mỗi tuần (trang này)</span>
            </div>
        </div>

        <!-- List -->
        <div v-loading="loading" class="tlc-rec-body">
            <div v-for="group in groups" :key="group.key" class="tlc-rec-group">
                <div class="tlc-rec-group__title">
                    <i class="el-icon-user"></i> {{ group.title }}
                    <span class="tlc-rec-group__count">{{ group.rules.length }}</span>
                </div>

                <div class="tlc-rec-grid">
                    <div
                        v-for="rule in group.rules"
                        :key="rule.id"
                        class="tlc-rec-card"
                        :style="cardStyle(rule)"
                    >
                        <div class="tlc-rec-card__head">
                            <span class="tlc-rec-card__type">
                                <i :class="metaOf(rule).icon"></i> {{ metaOf(rule).label }}
                            </span>
                            <span v-if="rule.is_locked" class="tlc-rec-card__lock" :title="lockHint(rule)">
                                <i class="el-icon-lock"></i> {{ rule.created_by_name || "Người khác đặt" }}
                            </span>
                        </div>
                        <div class="tlc-rec-card__title">{{ rule.title || metaOf(rule).label }}</div>
                        <div v-if="rule.team_lead_name" class="tlc-rec-card__name">
                            <i class="el-icon-user"></i> {{ rule.team_lead_name }}
                        </div>
                        <div class="tlc-rec-card__slots">
                            <span v-for="(c, i) in slotChips(rule)" :key="i" class="tlc-rec-card__chip">{{ c }}</span>
                        </div>
                        <div class="tlc-rec-card__range">
                            <i class="el-icon-date"></i> {{ rangeText(rule) }}
                            <el-tag v-if="isExpired(rule)" size="mini" type="info">Hết hạn</el-tag>
                        </div>
                        <div v-if="canManage(rule)" class="tlc-rec-card__actions">
                            <el-button size="mini" plain icon="el-icon-edit" @click="editRule(rule)">Sửa</el-button>
                            <el-button size="mini" plain type="danger" icon="el-icon-delete" @click="confirmDelete(rule)">Xoá</el-button>
                        </div>
                    </div>
                </div>
            </div>

            <el-empty
                v-if="!loading && !hasVisibleRules"
                :description="emptyText"
                :image-size="110"
            />
        </div>

        <el-pagination
            v-if="paging.total > paging.per_page"
            class="tlc-rec-pager"
            background
            layout="prev, pager, next"
            :current-page="paging.current_page"
            :page-size="paging.per_page"
            :total="paging.total"
            @current-change="onPageChange"
        />

        <fixed-schedule-form-dialog
            :visible.sync="formDialogVisible"
            :editing="formEditing"
            :initial="formInitial"
            :team-leads="tlcTeamLeads"
            :show-team-lead="showTeamLead"
            :submitting="formSubmitting"
            :current-user-id="currentUserId"
            @submit="handleFormSubmit"
        />
    </div>
</template>

<script>
import moment from "moment-timezone";
import { mapGetters } from "vuex";
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import {
    TLC_GET_SCHEDULES,
    TLC_GET_FIXED_SCHEDULES,
    TLC_CREATE_FIXED_SCHEDULE,
    TLC_UPDATE_FIXED_SCHEDULE,
    TLC_DELETE_FIXED_SCHEDULE,
} from "@/core/services/store/team-leads-calendar/teamLeadsCalendar.module";
import {
    WEEKDAY_SHORT,
    EVENT_TYPE,
    EVENT_TYPE_META,
    fixedScheduleRangeText,
    isFixedScheduleExpired,
} from "@/core/config/teamLeadsCalendar/teamLeadsCalendarOption";
import tlcRoleMixin from "./mixins/tlcRoleMixin";
import FixedScheduleFormDialog from "@/views/pages/teamlead-fixed-schedules/components/FixedScheduleFormDialog.vue";

export default {
    name: "TeamLeadsRecurringSchedules",
    components: { FixedScheduleFormDialog },
    mixins: [tlcRoleMixin],
    data() {
        return {
            rules: [],
            paging: { current_page: 1, per_page: 20, total: 0, last_page: 1 },
            loading: false,

            filterUserId: null,
            keyword: "",

            formDialogVisible: false,
            formInitial: {},
            formEditing: false,
            formSubmitting: false,
        };
    },
    computed: {
        ...mapGetters(["tlcTeamLeads", "tlcViewAll"]),
        /** `view_all` từ API quyết định có quản lý lịch của Teamlead khác hay không */
        showTeamLead() {
            return this.tlcViewAll !== false;
        },
        /** Lọc nội dung ở FE vì endpoint chưa công bố tham số tìm kiếm */
        filteredRules() {
            const kw = String(this.keyword).trim().toLowerCase();
            if (!kw) return this.rules;
            return this.rules.filter((r) =>
                `${r.title || ""} ${r.team_lead_name || ""}`.toLowerCase().includes(kw)
            );
        },
        /** Endpoint gắn lịch cố định theo từng user nên gom nhóm theo Teamlead */
        groups() {
            const map = new Map();
            this.filteredRules.forEach((rule) => {
                const key = rule.team_lead_id != null ? String(rule.team_lead_id) : "unknown";
                if (!map.has(key)) {
                    map.set(key, {
                        key,
                        title:
                            rule.team_lead_name ||
                            (rule.team_lead_id ? `#${rule.team_lead_id}` : "Chưa xác định"),
                        rules: [],
                    });
                }
                map.get(key).rules.push(rule);
            });
            return [...map.values()].sort((a, b) => a.title.localeCompare(b.title, "vi"));
        },
        hasVisibleRules() {
            return this.filteredRules.length > 0;
        },
        teamLeadCount() {
            return new Set(this.rules.map((r) => r.team_lead_id).filter((id) => id != null)).size;
        },
        weeklySlotCount() {
            return this.rules.reduce((acc, r) => acc + (r.slots || []).length, 0);
        },
        emptyText() {
            if (this.keyword) return "Không có lịch nào khớp từ khoá";
            return "Chưa có lịch cố định nào — bấm “Thêm lịch cố định” ở trên để tạo";
        },
    },
    mounted() {
        this.$store.dispatch(SET_BREADCRUMB, [
            { title: "Lịch làm việc Teamlead", route: "team-leads-calendar" },
            { title: "Lịch cố định" },
        ]);
        this.loadProfileDepartment();
        this.loadTeamLeads();
    },
    methods: {
        /**
         * Danh sách Teamlead + cờ `view_all` lấy từ `teamlead/schedules` của tuần hiện tại
         * (chưa có endpoint danh sách Teamlead riêng). Chạy xong mới tải danh sách lịch cố
         * định để biết có được phép gửi `user_id` hay không.
         */
        loadTeamLeads() {
            const from = moment().startOf("isoWeek").format("YYYY-MM-DD");
            const to = moment().endOf("isoWeek").format("YYYY-MM-DD");
            this.$store
                .dispatch(TLC_GET_SCHEDULES, { date_from: from, date_to: to })
                .catch(() => {})
                .then(() => this.fetchList());
        },
        fetchList() {
            this.loading = true;
            const params = { page: this.paging.current_page, per_page: this.paging.per_page };
            if (this.showTeamLead && this.filterUserId) params.user_id = this.filterUserId;
            this.$store
                .dispatch(TLC_GET_FIXED_SCHEDULES, params)
                .then(({ rows, paging }) => {
                    this.rules = rows;
                    this.paging = { ...this.paging, ...paging };
                    this.loading = false;
                })
                .catch((res) => {
                    this.rules = [];
                    this.loading = false;
                    this.$message.error(this.errorText(res, "Không tải được danh sách lịch cố định"));
                });
        },
        reload() {
            this.paging.current_page = 1;
            this.fetchList();
        },
        onPageChange(page) {
            this.paging.current_page = page;
            this.fetchList();
        },

        metaOf(rule) {
            return EVENT_TYPE_META[rule.type] || EVENT_TYPE_META[EVENT_TYPE.NOTE];
        },
        cardStyle(rule) {
            const m = this.metaOf(rule);
            return { "--c": m.color, "--cbg": m.bg };
        },
        /** Chip hiển thị theo khung giờ: "T2 08:00–10:00" */
        slotChips(rule) {
            return (rule.slots || []).map((s) => {
                const day = WEEKDAY_SHORT[s.weekday] || `T${s.weekday}`;
                if (!s.start_time) return day;
                return `${day} ${s.start_time}${s.end_time ? `–${s.end_time}` : ""}`;
            });
        },
        rangeText(rule) {
            return fixedScheduleRangeText(rule);
        },
        isExpired(rule) {
            return isFixedScheduleExpired(rule);
        },
        /** HR quản lý mọi lịch; Teamlead chỉ sửa được lịch do chính mình tạo */
        canManage(rule) {
            if (this.effectiveRole === "hr") return true;
            return rule.team_lead_id === this.currentUserId && !rule.is_locked;
        },
        lockHint(rule) {
            return rule.created_by_name
                ? `${rule.created_by_name} đặt hộ — chỉ HR chỉnh sửa được`
                : "Do người khác đặt hộ — chỉ HR chỉnh sửa được";
        },
        errorText(res, fallback) {
            const data = res && (res.data || res);
            return (data && data.message) || fallback;
        },

        openCreate() {
            this.formEditing = false;
            this.formInitial = this.filterUserId ? { team_lead_id: this.filterUserId } : {};
            this.formDialogVisible = true;
        },
        editRule(rule) {
            this.formEditing = true;
            this.formInitial = rule;
            this.formDialogVisible = true;
        },
        confirmDelete(rule) {
            this.$confirm(
                `Xoá lịch cố định "${rule.title || ""}"? Các công việc chưa diễn ra sinh ra từ lịch này cũng sẽ bị gỡ.`,
                "Xác nhận xoá",
                { confirmButtonText: "Xoá", cancelButtonText: "Huỷ", type: "warning" }
            )
                .then(() => this.remove(rule.id))
                .catch(() => {});
        },
        remove(id) {
            this.$store
                .dispatch(TLC_DELETE_FIXED_SCHEDULE, { id })
                .then(() => {
                    this.$message.success("Đã xoá lịch cố định");
                    // Lùi trang nếu vừa xoá bản ghi cuối cùng của trang
                    if (this.rules.length === 1 && this.paging.current_page > 1) {
                        this.paging.current_page -= 1;
                    }
                    this.fetchList();
                })
                .catch((res) => this.$message.error(this.errorText(res, "Xoá thất bại, vui lòng thử lại")));
        },
        handleFormSubmit({ editing, id, payload }) {
            this.formSubmitting = true;
            const action = editing ? TLC_UPDATE_FIXED_SCHEDULE : TLC_CREATE_FIXED_SCHEDULE;
            const body = editing ? { id, ...payload } : payload;
            this.$store
                .dispatch(action, body)
                .then(() => {
                    this.$message.success(editing ? "Đã cập nhật lịch cố định" : "Đã tạo lịch cố định");
                    this.formDialogVisible = false;
                    this.fetchList();
                })
                .catch((res) => this.$message.error(this.errorText(res, "Lưu thất bại, vui lòng thử lại")))
                .then(() => {
                    this.formSubmitting = false;
                });
        },
    },
};
</script>

<style scoped>
.tlc-rec-page {
    padding: 4px 2px 32px;
}

.tlc-rec-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    background: #fff;
    border: 1px solid #eef0f5;
    border-radius: 14px;
    padding: 14px 16px;
    margin-bottom: 12px;
}
.tlc-rec-head__left {
    display: flex;
    gap: 14px;
    align-items: flex-start;
}
.tlc-rec-back {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    gap: 3px;
    font-size: 12px;
    font-weight: 600;
    color: #6366f1;
    background: #eef2ff;
    border-radius: 8px;
    padding: 5px 9px;
    white-space: nowrap;
}
.tlc-rec-title {
    font-size: 16px;
    font-weight: 800;
    color: #1f2430;
}
.tlc-rec-sub {
    font-size: 12px;
    color: #6b7387;
    line-height: 1.6;
    margin-top: 3px;
    max-width: 620px;
}
.tlc-role {
    flex: 0 0 auto;
    font-size: 12px;
    font-weight: 700;
    padding: 4px 10px;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    gap: 5px;
    white-space: nowrap;
}
.tlc-role--hr {
    background: #eef2ff;
    color: #4f46e5;
}
.tlc-role--team_lead {
    background: #ecfdf5;
    color: #059669;
}
.tlc-role--viewer {
    background: #f1f5f9;
    color: #64748b;
}

.tlc-rec-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 10px;
    margin-bottom: 12px;
}
.tlc-rec-bar__actions {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
}
.tlc-rec-bar__filters {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
}
.tlc-rec-bar__select {
    width: 190px;
}
.tlc-rec-bar__search {
    width: 220px;
}

.tlc-rec-stats {
    display: flex;
    gap: 10px;
    margin-bottom: 16px;
    flex-wrap: wrap;
}
.tlc-rec-stat {
    background: #fff;
    border: 1px solid #eef0f5;
    border-radius: 12px;
    padding: 10px 16px;
    min-width: 120px;
}
.tlc-rec-stat__num {
    display: block;
    font-size: 20px;
    font-weight: 800;
    color: #1f2430;
}
.tlc-rec-stat__num--on {
    color: #059669;
}
.tlc-rec-stat__label {
    font-size: 11px;
    color: #8a92a6;
}

.tlc-rec-body {
    min-height: 200px;
}
.tlc-rec-group {
    margin-bottom: 22px;
}
.tlc-rec-group__title {
    font-size: 13px;
    font-weight: 700;
    color: #55607a;
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 10px;
}
.tlc-rec-group__title i {
    color: #6366f1;
}
.tlc-rec-group__count {
    background: #eef2ff;
    color: #4f46e5;
    font-size: 11px;
    padding: 0 7px;
    border-radius: 999px;
}

.tlc-rec-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 12px;
}
.tlc-rec-card {
    border: 1px solid #eef0f5;
    border-left: 3px solid var(--c, #6366f1);
    border-radius: 12px;
    padding: 14px;
    background: #fff;
    box-shadow: 0 4px 14px -10px rgba(31, 41, 55, 0.18);
    transition: opacity 0.15s ease, box-shadow 0.15s ease;
}
.tlc-rec-card:hover {
    box-shadow: 0 10px 24px -12px rgba(31, 41, 55, 0.28);
}
.tlc-rec-card__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
}
.tlc-rec-card__type {
    font-size: 10.5px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: var(--c, #6366f1);
}
.tlc-rec-card__lock {
    font-size: 10.5px;
    font-weight: 600;
    color: #94a0b8;
    display: inline-flex;
    align-items: center;
    gap: 3px;
    max-width: 140px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.tlc-rec-card__title {
    font-size: 14px;
    font-weight: 700;
    color: #1f2430;
    margin-top: 8px;
}
.tlc-rec-card__name {
    font-size: 11.5px;
    color: #8a92a6;
    margin-top: 3px;
}
.tlc-rec-card__slots {
    display: flex;
    flex-wrap: wrap;
    gap: 5px;
    margin: 10px 0;
}
.tlc-rec-card__chip {
    background: var(--cbg, #eef2ff);
    color: var(--c, #4f46e5);
    font-size: 11px;
    font-weight: 600;
    padding: 2px 9px;
    border-radius: 999px;
}
.tlc-rec-card__range {
    font-size: 11px;
    color: #8a92a6;
}
.tlc-rec-card__range i {
    margin-right: 3px;
}
.tlc-rec-card__actions {
    display: flex;
    gap: 6px;
    margin-top: 12px;
    padding-top: 10px;
    border-top: 1px dashed #eef0f5;
}

.tlc-rec-pager {
    margin-top: 4px;
    text-align: center;
}

@media (max-width: 720px) {
    .tlc-rec-head {
        flex-direction: column;
    }
    .tlc-rec-head__left {
        flex-direction: column;
        gap: 8px;
    }
    .tlc-rec-bar__select,
    .tlc-rec-bar__search {
        width: 100%;
    }
}
</style>
